import styled from '@emotion/styled';
import React, { PropsWithChildren, useMemo } from 'react';
import { semantic_colors, shapes } from '../../foundation';
import { FormLabel } from '../form';
import { List } from '../list';
import { ListItemProps } from '../list-item';
import { Popover } from '../popover';
import { useProviderConfig } from '../provider';
import { Stack } from '../stack';
import { DataTableColumnType } from '../table';
import { BaseText } from '../text';

export interface TableColumnViewType<DataType> extends DataTableColumnType<DataType> {
  isOn?: boolean;
}

export interface ColumnViewControllerProps<DataType> {
  /**
   * columns의 모든 width가 필수값이어야 함
   */
  columns: Array<TableColumnViewType<DataType>>;
  onChange: (columns: Array<TableColumnViewType<DataType>>) => void;
  width?: number;
  /**
   * 고정 column 변경 기능 사용 여부
   * @default true
   * @see maxFixedCount
   * @see pullsFixed
   */
  canFixed?: boolean;
  maxFixedCount?: number;
  /**
   * 고정 column을 별도로 끌어서 사용할 지 여부
   * @default false
   * @see noFixedItemsText
   * @see fixedSectionTitle
   */
  pullsFixed?: boolean;
  /** @default '고정 정보가 없습니다.' */
  noFixedItemsText?: string;
  /** @default '고정 정보' */
  fixedSectionTitle?: string;
  /** @default '목록 정보' */
  listSectionTitle?: string;
}

export const ColumnViewController = <T,>({
  children,
  columns,
  onChange,
  width = 200,
  canFixed = true,
  pullsFixed,
  maxFixedCount,
  noFixedItemsText,
  fixedSectionTitle,
  listSectionTitle,
}: PropsWithChildren<ColumnViewControllerProps<T>>): JSX.Element => {
  const { locale } = useProviderConfig();
  const { _columns, fixedColumns, listColumns } = useMemo(() => {
    const _columns = canFixed
      ? columns.map((column) => ({ ...column, isOn: column.isOn ?? false, fixed: column.fixed ?? false }))
      : columns.map((column) => ({ ...column, isOn: column.isOn ?? false, fixed: null }));
    return {
      _columns,
      fixedColumns: _columns.filter(({ fixed }) => fixed),
      listColumns: _columns.filter(({ fixed }) => !fixed),
    };
  }, [columns]);

  const sortByFixed = (items: Array<ListItemProps>) => {
    return items.sort((a, b) => (a.fixed === b.fixed ? 0 : a.fixed ? -1 : 1));
  };
  const handleColumnsChange = (
    items: Array<ListItemProps<Extract<keyof T, string>>>,
    others: Array<ListItemProps<Extract<keyof T, string>>> = [],
  ) => {
    const _items = [...others, ...items];

    if (maxFixedCount && _items.filter(({ fixed }) => fixed).length > maxFixedCount) {
      return;
    }
    if (pullsFixed) {
      sortByFixed(_items);
    }
    onChange(_items);
  };

  return (
    <Stack direction='row'>
      <StyledPopover
        arrow={false}
        content={
          <>
            {pullsFixed && (
              <Stack direction='column' gap={12}>
                <FormLabel>
                  {fixedSectionTitle ?? locale?.ColumnViewController.fixedSectionTitle}
                  {!!maxFixedCount &&
                    ` (${locale?.ColumnViewController.max} ${maxFixedCount}${locale?.ColumnViewController.items})`}
                </FormLabel>
                {fixedColumns.length === 0 ? (
                  <EmptyFixed direction='row' height={40} align='center' justify='center'>
                    <BaseText kind='Body_12_Medium' color={semantic_colors.content.tertiary}>
                      {noFixedItemsText ?? locale?.ColumnViewController.noFixedItemsText}
                    </BaseText>
                  </EmptyFixed>
                ) : (
                  <List<Extract<keyof T, string>>
                    // @ts-expect-error: TS2322
                    items={fixedColumns}
                    // @ts-expect-error: TS2345
                    onChange={(items) => handleColumnsChange(items, listColumns)}
                    width={width}
                    draggable
                  />
                )}
              </Stack>
            )}
            <Stack direction='column' mt={pullsFixed ? 16 : 0} gap={12}>
              <FormLabel>{listSectionTitle ?? locale?.ColumnViewController.listSectionTitle}</FormLabel>
              <List<Extract<keyof T, string>>
                // @ts-expect-error: TS2322
                items={pullsFixed ? listColumns : _columns}
                onChange={(items) =>
                  // @ts-expect-error: TS2345
                  pullsFixed ? handleColumnsChange(items, fixedColumns) : handleColumnsChange(items)
                }
                width={width}
                draggable
              />
            </Stack>
          </>
        }
      >
        {children}
      </StyledPopover>
    </Stack>
  );
};

const EmptyFixed = styled(Stack)`
  ${shapes.border_radius.small};
  border: 1px dashed ${semantic_colors.border.primary};
`;

const StyledPopover = styled(Popover)`
  max-height: 576px;
  overflow-y: auto;
`;
